import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// Custom Heart Pulse Icon
const HeartPulseIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <path d="M19.5 12.572l-7.5 7.428l-7.5-7.428a5 5 0 1 1 7.5-6.566a5 5 0 1 1 7.5 6.572" />
    <path d="M3 12h4l2-3l3 6l2-3h7" />
  </svg>
);

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // El mail de compra trae el mail del comprador en el link (?email=...).
  // Precargarlo evita el error más común: registrarse con otro mail distinto
  // del que se usó para pagar, que el sistema rechaza.
  useEffect(() => {
    const emailDelLink = searchParams.get('email');
    if (emailDelLink) setEmail(emailDelLink.trim().toLowerCase());
  }, [searchParams]);

  // Check if coming from guest evaluation or limit reached
  const fromGuest = searchParams.get('from') === 'guest';
  const limitReached = searchParams.get('limit') === 'reached';
  const [hasGuestEvaluations, setHasGuestEvaluations] = useState(false);
  const [guestEvaluationsCount, setGuestEvaluationsCount] = useState(0);
  
  useEffect(() => {
    // Check for guest evaluations (new format - array)
    const guestEvals = localStorage.getItem('guest_evaluations');
    if (guestEvals) {
      const evals = JSON.parse(guestEvals);
      if (evals.length > 0) {
        setHasGuestEvaluations(true);
        setGuestEvaluationsCount(evals.length);
      }
    }
    // Also check old format for backwards compatibility
    const guestEval = localStorage.getItem('guest_evaluation');
    if (guestEval && !guestEvals) {
      setHasGuestEvaluations(true);
      setGuestEvaluationsCount(1);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    // Tiene que coincidir con el mínimo del servidor (UserRegister en
    // server.py). Con 6 acá y 8 allá, una contraseña de 6 o 7 pasaba este
    // control y el servidor la rechazaba con un error técnico.
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);

    try {
      const data = await register(email, password, name);
      
      // If user has guest evaluations (new format), save them to the backend
      const guestEvaluations = localStorage.getItem('guest_evaluations');
      if (guestEvaluations) {
        try {
          const evals = JSON.parse(guestEvaluations);
          for (const evalData of evals) {
            const evalToSave = {
              ...evalData,
              activity_level: evalData.activity_level || 'sedentario',
            };
            delete evalToSave.created_at;
            await api.createEvaluation(evalToSave);
          }
          localStorage.removeItem('guest_evaluations');
          localStorage.removeItem('guest_evaluations_count');
          toast.success(`${evals.length} entrevista(s) guardada(s) en tu cuenta`);
        } catch (evalErr) {
          console.error('Error saving guest evaluations:', evalErr);
        }
      }
      
      // Also check old format for backwards compatibility
      const guestEvaluation = localStorage.getItem('guest_evaluation');
      if (guestEvaluation && !guestEvaluations) {
        try {
          const evalData = JSON.parse(guestEvaluation);
          const evalToSave = {
            ...evalData,
            activity_level: evalData.activity_level || 'sedentario',
          };
          delete evalToSave.created_at;
          
          await api.createEvaluation(evalToSave);
          localStorage.removeItem('guest_evaluation');
          localStorage.removeItem('guest_evaluation_used');
          toast.success('Entrevista guardada en tu cuenta');
        } catch (evalErr) {
          console.error('Error saving guest evaluation:', evalErr);
        }
      }
      
      toast.success('Cuenta creada correctamente');
      navigate('/alumnos');
    } catch (err) {
      setError(err.message || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex flex-col">
      {/* Main Content - Centered */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Header */}
          <div className="text-center mb-10">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <HeartPulseIcon className="w-14 h-14 text-[#1F3A5F]" />
            </div>

            {/* Product Name */}
            <h1 className="text-2xl font-bold text-[#1F3A5F] tracking-wider uppercase mb-0.5 font-['Inter']">
              EVALPRO
            </h1>

            {/* System Title */}
            <p className="text-xs font-medium text-[#1F3A5F]/60 tracking-wide uppercase mb-1 font-['Inter']">
              Sistema de Entrevista Profesional
            </p>

            {/* Brand */}
            <p className="text-sm text-gray-500 font-['Inter']">
              Ejercicio Físico y Salud
            </p>
            
            {/* Register heading */}
            <h2 className="text-lg font-semibold text-[#1F3A5F] mt-6 font-['Inter']">
              {limitReached ? 'Crea una cuenta para continuar' : 'Registrarse'}
            </h2>
            <p className="mt-1 text-sm text-gray-500 font-['Inter']" data-testid="register-same-email-hint">
              Usá el mismo mail con el que pagaste.
            </p>

            {/* Limit reached notice */}
            {limitReached && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-['Inter']">
                  Has utilizado las 2 entrevistas gratuitas. Registrate para guardar tu progreso.
                </p>
              </div>
            )}
            
            {/* Guest evaluation notice */}
            {hasGuestEvaluations && !limitReached && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm font-['Inter']">
                    {guestEvaluationsCount === 1 
                      ? 'Tu entrevista se guardará automáticamente'
                      : `Tus ${guestEvaluationsCount} entrevistas se guardarán automáticamente`
                    }
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-200" data-testid="register-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="font-['Inter']">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm text-gray-700 font-['Inter']">
                Nombre completo
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 bg-white border-gray-200 rounded-lg font-['Inter']"
                placeholder="Tu nombre"
                required
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-gray-700 font-['Inter']">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-white border-gray-200 rounded-lg font-['Inter']"
                placeholder="tu@email.com"
                required
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm text-gray-700 font-['Inter']">
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-white border-gray-200 rounded-lg pr-10 font-['Inter']"
                  placeholder="••••••••"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm text-gray-700 font-['Inter']">
                Confirmar contraseña
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-11 bg-white border-gray-200 rounded-lg pr-10 font-['Inter']"
                  placeholder="••••••••"
                  required
                  data-testid="register-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 bg-[#1F3A5F] hover:bg-[#2a4a73] text-white font-medium rounded-lg font-['Inter']" 
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registrando...
                </>
              ) : (
                'Registrarse'
              )}
            </Button>
          </form>

          {/* Link */}
          <p className="mt-8 text-center text-sm text-gray-500 font-['Inter']">
            ¿Ya tenés cuenta?{' '}
            <Link 
              to="/login" 
              className="text-[#1F3A5F] font-medium hover:underline"
              data-testid="login-link"
            >
              Iniciá sesión
            </Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-xs text-gray-400 font-['Inter']">
          © 2025 Ejercicio Físico y Salud Argentina
        </p>
      </footer>
    </div>
  );
}
